import { TFunction } from "react-i18next";
import { useMemo } from "react";
import * as yup from "yup";
import {
  Attribute,
  CameraAttributes,
  CameraAttributeValues,
  CameraEntity,
  CameraHidePassword,
  CameraLabelValues,
  CameraTypes,
  CameraTypesValues,
  CameraVendorValues,
  GenericEntityCredentials,
  HvsEntity,
  PresetAttributes,
  VmsAttributes,
} from "lib/api";
import { CameraUtils } from "lib/utils/cameraUtils";
import { UserPolicyTypes } from "lib/api/userSettings";
import { VmsOptions } from "lib/api/hvsConfigs";

export const useCameraValidation = (
  t: TFunction<"cameraAddEdit">
): yup.AnySchema => {
  return useMemo(
    () =>
      yup.object().shape(
        {
          name: yup.string().trim().required(t("validation.required")),
          description: yup.string().trim(),

          // type: yup.number().required(t("validation.required")),
          ipAddress: yup.string(),
          port: yup
            .number()
            .min(0, t("validation.portOutOfRange"))
            .max(65535, t("validation.portOutOfRange")),
          username: yup.string().when(["password"], {
            is: (password: string) => password != null,
            then: yup.string().required(t("validation.required")),
          }),
          password: yup.string().when(["username"], {
            is: (username: string) => username != null,
            then: yup.string().required(t("validation.required")),
          }),
          streamIpAddress: yup.string(),
          streamPort: yup
            .number()
            .min(0, t("validation.portOutOfRange"))
            .max(65535, t("validation.portOutOfRange")),
          customStreamUrl: yup.string(),
          overSSL: yup.boolean(),
          microphone: yup.boolean(),
          address: yup.string().when(["longitude", "latitude"], {
            is: (longitude: string, latitude: string) =>
              latitude != null && longitude != null,
            then: yup.string().required(t("validation.required")),
          }),
          latitude: yup
            .number()
            .typeError(t("validation.latitudeOutOfRange"))
            .min(-90, t("validation.latitudeOutOfRange"))
            .max(90, t("validation.latitudeOutOfRange"))
            .when(["longitude", "findCameras"], {
              is: (longitude: string, findCameras: boolean) =>
                findCameras || longitude != null,
              then: yup
                .number()
                .typeError(t("validation.latitudeOutOfRange"))
                .required(t("validation.required")),
            }),
          longitude: yup
            .number()
            .typeError(t("validation.longitudeOutOfRange"))
            .min(-180, t("validation.longitudeOutOfRange"))
            .max(180, t("validation.longitudeOutOfRange"))
            .when(["latitude", "findCameras"], {
              is: (latitude: string, findCameras: boolean) =>
                findCameras || latitude != null,
              then: yup
                .number()
                .typeError(t("validation.longitudeOutOfRange"))
                .required(t("validation.required")),
            }),
          altitude: yup
            .number()
            .integer(t("validation.altitudeNotInteger"))
            .nullable()
            .typeError(t("validation.altitudeNotInteger")),
          // findCameras: yup.boolean(),
          // radius: yup.number().when("findCameras", {
          //   is: true,
          //   then: yup
          //     .number()
          //     .typeError(t("validation.radiusNotPositiveNumber"))
          //     .positive(t("validation.radiusNotPositiveNumber"))
          //     .required(t("validation.required")),
          // }),
          presetsPositioning: yup.array().of(
            yup.object().shape({
              azimuth: yup
                .number()
                .typeError(t("validation.azimuthOutOfRange"))
                .nullable()
                .min(0, t("validation.azimuthOutOfRange"))
                .max(360, t("validation.azimuthOutOfRange")),
            })
          ),
          attributes: yup.array().of(
            yup.object().shape({
              id: yup.string(),
              label: yup.string().required(t("validation.required")),
              type: yup.string().nullable(),
              value: yup
                .mixed()
                .when("type", {
                  is: "boolean",
                  then: yup.boolean(),
                })
                .when("type", {
                  is: "numeric",
                  then: yup.number().typeError(t("validation.valueNotANumber")),
                })
                .when("type", {
                  is: "hyperlink",
                  then: yup.string().url(t("validation.iconUrlNotValid")),
                }),
            })
          ),
          intFpsValue: yup
            .number()
            .integer(t("validation.altitudeNotInteger"))
            .nullable()
            .typeError(t("validation.altitudeNotInteger")),
        },
        [
          ["latitude", "longitude"],
          ["username", "password"],
        ]
      ),
    [t]
  );
};
export const useCameraInitialValues = (
  camera: CameraEntity | undefined,
  cameraType: HvsEntity | undefined,
  vms: HvsEntity | undefined,
  vmsConfig: VmsOptions | undefined,
  presets: HvsEntity[] | undefined,
  hasSystemTypePolicy: (
    typeName: string,
    policyAction: UserPolicyTypes
  ) => boolean
): CameraFormData => {
  const attrKeys = Object.keys(cameraType?.attributes || {});
  const attrValues = Object.values(cameraType?.attributes || {});
  return useMemo((): CameraFormData => {
    return {
      canEdit: hasSystemTypePolicy(
        cameraType?.id ?? "",
        UserPolicyTypes.insertupdate
      ),
      name: camera?.name ?? "",
      type:
        (camera?.attributes.type.value as string)?.toLowerCase() ===
        CameraAttributeValues.RTSP
          ? CameraTypes.RTSP
          : (camera?.attributes.type.value as string)?.toLowerCase() ===
            CameraAttributeValues.Onvif
          ? CameraTypes.Onvif
          : camera
          ? CameraTypes.VMS
          : CameraTypes.RTSP,
      description: camera?.description ?? "",
      address: camera?.address ?? "",
      customStreamUrl:
        (camera?.attributes[CameraAttributes.StreamingEndPoint]
          ?.value as string) ?? "",
      ipAddress:
        (camera?.attributes[CameraAttributes.IpAddress]?.value as string) ?? "",
      latitude: camera?.location?.coordinates[1],
      longitude: camera?.location?.coordinates[0],
      id: camera?.id ?? "",
      sourceId: camera?.source_id ?? "",
      microphone:
        camera?.attributes[CameraAttributes.Microphone]?.value === true,
      overSSL: camera?.attributes[CameraAttributes.Secure]?.value === true,
      vmsHasPlayback: vmsConfig?.has_playback,
      vmsFPSPlayback: vmsConfig?.requires_fps_for_playback,
      canPlayback: camera?.attributes[CameraAttributes.CanPlayback]
        ?.value as boolean,
      recording:
        camera?.attributes[CameraAttributes.CanPlayback]?.value === true
          ? true
          : camera?.attributes[CameraAttributes.CanPlayback]?.value === false
          ? false
          : camera?.attributes[CameraAttributes.Recording]?.value === true,
      username: camera?.credentials?.username ?? "",
      hasPassword: camera?.credentials?.blank_password !== true,
      password:
        (camera?.credentials?.blank_password ?? true) !== true
          ? CameraHidePassword
          : "",
      port: (camera?.attributes[CameraAttributes.Port]?.value as string) ?? "",
      ptz: camera?.attributes[CameraAttributes.Ptz]?.value === true,
      intFpsValue:
        (camera?.attributes[CameraAttributes.intFpsValue]?.value as number) ??
        "",
      streamIpAddress:
        (camera?.attributes[CameraAttributes.StreamingIp]?.value as string) ??
        "",
      streamPort:
        (camera?.attributes[CameraAttributes.StreamingPort]?.value as string) ??
        "",
      updateTime: camera?.update_timestamp,
      presetPositioningSectionShow:
        ((vms?.attributes[VmsAttributes.SlewEnabled]?.value as boolean) ??
          false) &&
        ((camera?.attributes[CameraAttributes.Ptz]?.value as boolean) ?? false),
      slewToCue:
        (camera?.attributes[CameraAttributes.SlewEnabled]?.value as boolean) ??
        false,
      altitude:
        (camera?.attributes[CameraAttributes.PositioningAltitude]
          ?.value as number) ?? "",
      presetsPositioning: presets?.map((p) => {
        return {
          name: p.name,
          id: p.id,
          azimuth: (p?.attributes[PresetAttributes.Azimuth]?.value ??
            "") as string,
        };
      }),
      status: CameraUtils.getStatus(camera),
      attributes: attrValues.map((attr, index) => ({
        id: attrKeys[index],
        order: index,
        ...attr,
        value: "",
        ...(camera?.attributes[attrKeys[index]] ?? {}),
        appearance: attr.appearance,
      })),

      /* name: camera?.name ?? "",
      type:
        (camera?.attributes.type.value as string)?.toLowerCase() ===
        CameraAttributeValues.RTSP
          ? CameraTypes.RTSP
          : (camera?.attributes.type.value as string)?.toLowerCase() ===
            CameraAttributeValues.Onvif
          ? CameraTypes.Onvif
          : CameraTypes.RTSP,
      description: camera?.description ?? "",
      address: camera?.address ?? "",
      customStreamUrl:
        (camera?.attributes[CameraAttributes.StreamingEndPoint]
          ?.value as string) ?? "",
      ipAddress:
        (camera?.attributes[CameraAttributes.IpAddress]?.value as string) ?? "",
      latitude: camera?.location?.coordinates[1],
      longitude: camera?.location?.coordinates[0],
      id: camera?.id ?? "",
      sourceId: camera?.source_id,
      microphone:
        camera?.attributes[CameraAttributes.Microphone]?.value === true,
      overSSL: camera?.attributes[CameraAttributes.Secure]?.value === true,
      vmsHasPlayback: false,
      canPlayback: camera?.attributes[CameraAttributes.CanPlayback]
        ?.value as boolean,
      recording:
        camera?.attributes[CameraAttributes.CanPlayback]?.value === true
          ? true
          : camera?.attributes[CameraAttributes.CanPlayback]?.value === false
          ? false
          : camera?.attributes[CameraAttributes.Recording]?.value === true,
      username: camera?.credentials?.username ?? "",
      hasPassword: camera?.credentials?.blank_password !== true,
      password:
        (camera?.credentials?.blank_password ?? true) !== true ? "••••••" : "",
      port: (camera?.attributes[CameraAttributes.Port]?.value as string) ?? "",
      ptz: camera?.attributes[CameraAttributes.Ptz]?.value === true,
      streamIpAddress:
        (camera?.attributes[CameraAttributes.StreamingIp]?.value as string) ??
        "",
      streamPort:
        (camera?.attributes[CameraAttributes.StreamingPort]?.value as string) ??
        "",
      updateTime: camera?.update_timestamp,
      presetPositioningSectionShow: false,
      slewToCue:
        (camera?.attributes[CameraAttributes.SlewEnabled]?.value as boolean) ??
        false,
      altitude:
        (camera?.attributes[CameraAttributes.PositioningAltitude]
          ?.value as number) ?? "",

      status: CameraUtils.getStatus(camera),
      attributes: attrValues.map((attr, index) => ({
        id: attrKeys[index],
        order: index,
        ...attr,
        value: "",
        ...(camera?.attributes[attrKeys[index]] ?? {}),
        appearance: attr.appearance,
      })),

      canEdit: hasSystemTypePolicy(
        cameraType?.id ?? "",
        UserPolicyTypes.insertupdate
      ), */
    };
  }, [
    attrKeys,
    attrValues,
    camera,
    cameraType?.id,
    hasSystemTypePolicy,
    presets,
    vms?.attributes,
    vmsConfig?.has_playback,
    vmsConfig?.requires_fps_for_playback,
  ]);
};
export const cameraFromToCameraEntity = (
  data: CameraFormData,
  camera: HvsEntity | undefined,
  initialValues: CameraFormData
): Partial<CameraEntity> => {
  const lat = Number.parseFloat(data.latitude as unknown as string);
  const lng = Number.parseFloat(data.longitude as unknown as string);
  let credentials = {
    credentials: undefined as GenericEntityCredentials | undefined,
  };
  const hasLocation = !Number.isNaN(lat) && !Number.isNaN(lng);
  const attributes: Record<string, Attribute> = camera?.attributes ?? {};
  if (data.attributes && data.attributes.length > 0)
    data.attributes?.forEach((attr: Attribute) => {
      // exclude id from the object sent to the server
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { id: attrId, type, value, label } = attr;
      // if a value is set add attribute
      if (value !== undefined && value !== null && value !== "") {
        let formattedFinalDate;
        if (type === "date and time") {
          formattedFinalDate = value;
          /* if (value !== camera?.data.attributes[`${attr.id}`]?.value) {
              const objData = new Date(value as string);
              const objDate = {
                date: moment(objData).format("YYYY-MM-DD"),
                hours: moment(objData).get("hours"),
                minutes: moment(objData).get("minutes"),
                seconds: moment(objData).get("seconds"),
              };

              // eslint-disable-next-line camelcase
              const final_date = moment(
                `${objDate.date} ${objDate.hours}:${objDate.minutes}:${objDate.seconds}`
              ).format(standardDateFormat);
              formattedFinalDate = moment(
                getDateTimeInUTCByZoneWithoutFormat(final_date, timezone)
              ).format(standardDateFormat);
            } else {
              formattedFinalDate = value as string;
            } */
        }
        attributes[`${attr.id}`] = {
          appearance: undefined,
          label,
          type,
          value:
            value && type === "numeric"
              ? Number(value)
              : type === "date and time"
              ? formattedFinalDate
              : value,
          contentType: attr.contentType ? attr.contentType : undefined,
        };
      } else {
        delete attributes[`${attr.id}`];
      }
    });
  if (data.type === CameraTypes.Onvif || data.type === CameraTypes.RTSP) {
    // always set camera vendor attribute
    // only ip/onvif (hitachi) cameras can be added
    attributes[CameraAttributes.Vendor] = {
      appearance: undefined,
      label: CameraLabelValues.VendorModel,
      type: "text",
      value: CameraVendorValues.Hitachi,
    };
    if (data.ipAddress && data.ipAddress !== "") {
      attributes[CameraAttributes.IpAddress] = {
        appearance: undefined,
        label: CameraAttributes.IpAddress,
        type: "text",
        value: data.ipAddress,
      };
    } else {
      delete attributes[CameraAttributes.IpAddress];
    }
    if (data.port && data.port !== "") {
      attributes[CameraAttributes.Port] = {
        appearance: undefined,
        label: CameraAttributes.Port,
        type: "text",
        value: data.port,
      };
    } else {
      delete attributes[CameraAttributes.Port];
    }
    if (data.type === CameraTypes.Onvif) {
      if (data.streamPort && data.streamPort !== "") {
        attributes[CameraAttributes.StreamingIp] = {
          appearance: undefined,
          label: CameraAttributes.StreamingIp,
          type: "text",
          value: data.streamIpAddress,
        };
      } else {
        delete attributes[CameraAttributes.StreamingIp];
      }
      if (data.streamPort && data.streamPort !== "") {
        attributes[CameraAttributes.StreamingPort] = {
          appearance: undefined,
          label: CameraAttributes.StreamingPort,
          type: "text",
          value: data.streamPort,
        };
      } else {
        delete attributes[CameraAttributes.StreamingPort];
      }
    }
    if (data.customStreamUrl && data.customStreamUrl !== "") {
      attributes[CameraAttributes.StreamingEndPoint] = {
        appearance: undefined,
        label: CameraAttributes.StreamingEndPoint,
        type: "text",
        value: data.customStreamUrl,
      };
    } else {
      delete attributes[CameraAttributes.StreamingEndPoint];
    }
    if (data.overSSL) {
      attributes[CameraAttributes.Secure] = {
        appearance: undefined,
        label: CameraAttributes.Secure,
        type: "bool",
        value: data.overSSL,
      };
    } else {
      delete attributes[CameraAttributes.Secure];
    }
    if (data.microphone) {
      attributes[CameraAttributes.Microphone] = {
        appearance: undefined,
        label: CameraAttributes.Microphone,
        type: "bool",
        value: data.microphone,
      };
    } else {
      delete attributes[CameraAttributes.Microphone];
    }
    attributes[CameraAttributes.Type] = {
      appearance: undefined,
      label: CameraAttributes.Type,
      type: "text",
      value:
        data.type === CameraTypes.RTSP
          ? CameraTypesValues.RTSP
          : CameraTypesValues.Onvif,
    };
    if (
      data.username !== undefined &&
      data.username !== null &&
      data.username !== ""
    ) {
      credentials = {
        credentials: {
          username: data.username,
          password:
            initialValues.password !== data.password ? data.password ?? "" : "",
          blank_password: !data.password || data.password === "",
        },
      };
    }
  }
  if (data.type === CameraTypes.VMS && data.vmsHasPlayback) {
    if (
      data.recording !== undefined &&
      (data.canPlayback === null || data.canPlayback === undefined)
    ) {
      attributes[CameraAttributes.Recording] = {
        appearance: undefined,
        label: CameraAttributes.Recording,
        type: "bool",
        value: data.recording,
      };
    } else {
      delete attributes[CameraAttributes.Recording];
    }
    if (data.vmsFPSPlayback === true && data.intFpsValue) {
      attributes[CameraAttributes.intFpsValue] = {
        appearance: undefined,
        label: CameraAttributes.intFpsValue,
        type: "number",
        value: data.intFpsValue,
      };
    } else {
      delete attributes[CameraAttributes.intFpsValue];
    }
  }
  if (data.type === CameraTypes.VMS && data.presetPositioningSectionShow) {
    if (data.altitude) {
      attributes[CameraAttributes.PositioningAltitude] = {
        appearance: undefined,
        label: CameraAttributes.PositioningAltitude,
        type: "numeric",
        value: data.altitude,
      };
    } else {
      delete attributes[CameraAttributes.PositioningAltitude];
    }
    if (data.slewToCue) {
      attributes[CameraAttributes.SlewEnabled] = {
        appearance: undefined,
        label: CameraAttributes.SlewEnabled,
        type: "bool",
        value: data.slewToCue,
      };
    } else {
      delete attributes[CameraAttributes.SlewEnabled];
    }
  } else {
    delete attributes[CameraAttributes.PositioningAltitude];
    delete attributes[CameraAttributes.SlewEnabled];
  }
  return {
    name: data.name,
    description: data.description,
    attributes,
    update_timestamp: new Date(),
    ...credentials,
    ...(hasLocation
      ? {
          address: data.address,
          location: {
            type: "Point",
            coordinates: [lng, lat],
          },
        }
      : {
          address: "",
          location: undefined,
        }),
  };
};
